(ns hai.tasks.form
  "The task-composing pieces shared by the Tasks mini-app cards: [[task-form]],
   the title form that typing expands into the full composer — the title
   becomes a heading (click it to edit again) over the dashed optional extras:
   description, link, assignment, deadline and tags. Each stages locally and
   they all reach `on-save` together, in one payload. Unlike notes nothing
   auto-saves. Mirrors [[hai.notes.form]]."
  (:require
   [clojure.string :as str]
   [hai.entity.draft :as draft]
   [hai.entity.form :as entity-form]
   [hai.entity.link-combobox :as link-combobox]
   [hai.entity.sections :as sections]
   [hai.tags.combobox :as tag-combobox]
   [hai.tasks.deadline :as deadline]
   [hai.tasks.urgency :as urgency]
   [hai.ui.components.chip :as chip-ui]
   [hai.ui.components.click-outside :as click-outside]
   [hai.ui.components.date-picker :as date-picker]
   [hai.ui.components.datetime :as datetime]
   [hai.ui.icons :as icons]
   [hai.ui.keyboard :as keyboard]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private person-icon (icons/mui-icon :outlined :person))
(def ^:private deadline-icon (icons/mui-icon :outlined :event))
(def ^:private urgency-icon (icons/mui-icon :outlined :bolt))
(def ^:private details-icon (icons/mui-icon :outlined :subject))

(defn- first-email
  "Email attrs arrive as a collection on a resolved entity, as a bare string
   when someone typed one into the picker."
  [email]
  (not-empty (if (coll? email) (first email) email)))

(defn- assignee-label
  "Names whoever the composer is holding — a contact the picker staged, or the
   user an existing task is already assigned to."
  [assignee]
  (or (not-empty (:contact/full-name assignee))
      (not-empty (:user/full-name assignee))
      (first-email (:contact/email assignee))
      (first-email (:user/email assignee))
      "Assignee"))

(defn- keep-picker-focus!
  "Mousedown anywhere in the open picker keeps focus where it is: the title
   editor's blur reflows the layout mid-click, and the click then never
   completes on the moved day cell or button — the same trap every pill in this
   form guards against. The clock fields are exempt; they are typed into, so
   they do need the focus."
  [event]
  (when-not (.closest (.-target event) "input, textarea, select")
    (.preventDefault event)))

(def ^:private default-deadline-time-minutes (* 9 60))

(defn- with-implied-date
  "A time on its own is a deadline today. Applied when the picker is done, not
   while it is open, so the date doesn't appear under the user mid-pick."
  [deadline]
  (cond-> deadline
    (and (:time deadline) (nil? (:date deadline)))
    (assoc :date (date-picker/today-date))))

(defn- caret-to-end
  "Re-editing continues from the end of the text, not the start."
  [e]
  (let [el (.-target e)
        end (.. el -value -length)]
    (set! (.-selectionStart el) end)
    (set! (.-selectionEnd el) end)))

;; The dashed ghost pill for the optional extras — the notes "＋ tag" chip,
;; plus sizing for the icon-led pills (assignee, deadline).
(o/defstyled detail-pill sections/add-section-pill
  {:box-sizing "border-box"
   :height "24px"}
  [:svg {:width "14px" :height "14px" :fill "currentColor"}]
  ;; A pill to look at, not press — the assignee for a contributor.
  [:&.locked
   {:cursor "default" :opacity "0.6"}
   [:&:hover {:background "transparent" :border-color "rgba(50,64,37,0.3)"}]])

;; Set date | Set time as one dashed pill split by a divider — the notes
;; chip's border, radius and type, with two segments inside. Each segment is a
;; `.half` holding its button and, once picked, an inline ✕; the pill stays one
;; joined component either way. Empty it reads as an invitation (dashed); once
;; either half carries a value it reads as a chip (`.filled`).
(o/defstyled deadline-pill :span
  {:display "inline-flex" :align-items "stretch"
   :align-self "center"
   :box-sizing "border-box"
   :height "24px"
   :border-width "1px" :border-style "dashed" :border-color "rgba(50,64,37,0.3)"
   :border-radius "7px"}
  [:button
   {:display "inline-flex" :align-items "center" :gap "4px"
    :padding "3px 10px"
    :border "none" :background "transparent"
    :font-family "inherit"
    :font-size "12px" :font-weight "600" :line-height "1"
    :color "rgba(74,93,63,0.82)"
    :cursor "pointer"
    :transition "background 0.15s"}
   [:&:hover {:background "rgba(50,64,37,0.06)"}]
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.half
   {:display "inline-flex" :align-items "stretch"}
   [:&:first-child {:border-radius "7px 0 0 7px"}
    [:button:first-child {:border-radius "7px 0 0 7px"}]]
   [:&:last-child {:border-radius "0 7px 7px 0"}
    [:button:last-child {:border-radius "0 7px 7px 0"}]]
   ;; No fill on the picked half — the value's own weight and its ✕ say it's
   ;; set, the way a chip's label does.
   [:&.active
    [:button {:color t/--card-forest}]
    [:button:hover {:background "rgba(50,64,37,0.10)"}]]]
  [:.seg-remove
   {:padding "3px 8px 3px 0"}]
  [:.split-divider
   {:border-left-width "1px" :border-left-style "dashed"
    :border-left-color "rgba(50,64,37,0.3)"}]
  ;; Carrying a value, the pill is a chip: same hairline outline, same solid
  ;; divider, still no fill.
  [:&.filled
   {:border-style "solid" :border-color t/--note-sage-55}
   [:.split-divider
    {:border-left-style "solid" :border-left-color t/--note-sage-55}]])

;; ⚡ Now | Next | Later inside one dashed pill — the deadline pill's shape,
;; with the picked segment outlined like a chip.
(o/defstyled urgency-pill deadline-pill
  {:align-items "center" :gap "2px" :padding "0 3px"}
  [:.urgency-glyph
   {:display "inline-flex" :align-items "center"
    :padding-left "4px" :color t/--link-muted}
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  ;; All three carry the outline; only the picked one colours it in. Sizing them
  ;; alike keeps the pill from shifting as the pick moves along it.
  [:button
   {:height "18px" :padding "0 8px" :border-radius "5px"
    :border-width "1px" :border-style "solid" :border-color "transparent"}
   [:&:first-child {:border-radius "5px"}]
   [:&:last-child {:border-radius "5px"}]
   [:&.active
    {:border-color t/--note-sage-55 :color t/--card-forest}]])

;; The frame an inline editor takes when it opens among rows — the board's row
;; editor, the mini-app cards, the sidebar. A snow sheet inside the chips'
;; hairline: the form reads as one object, and where it ends is never a guess.
;; The frame is the editor's only chrome, so the ✕ that leaves it lives here —
;; on a line of its own above the title, where it can't crowd a title that runs
;; the width of the form.
(o/defstyled editor-frame :div
  {:background t/--colour-snow
   :border-radius "12px"
   :border-width "1px" :border-style "solid" :border-color t/--note-sage-55}
  [:.editor-close-row
   {:display "flex" :justify-content "flex-end"
    :padding "6px 10px 0"}]
  [:.editor-close
   {:width "32px" :height "32px" :padding "0"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "999px"
    :background "transparent"
    :color t/--link-muted
    :cursor "pointer"
    :transition "background 0.15s ease, color 0.15s ease"}
   [:&:hover {:background "rgba(50,64,37,0.08)" :color t/--card-forest}]
   [:svg {:width "22px" :height "22px" :fill "currentColor"}]]
  ([{:keys [on-close]} & children]
   (into (cond-> [:<>]
           on-close (conj [:div.editor-close-row
                           [:button.editor-close
                            {:type "button"
                             :aria-label "Close"
                             :title "Close"
                             ;; The focused title or details editor blurs on
                             ;; mousedown and reflows the form, which would move
                             ;; this button out from under the click.
                             :on-mouse-down #(.preventDefault %)
                             :on-click on-close}
                            [icons/outlined-close {:size 22}]]]))
         children)))

(o/defstyled shell :div
  {:padding "6px 16px 18px"}
  ;; The input chrome lives on the wrap so the → can sit inside it as a
  ;; full-height right segment that grows with the field.
  [:.title-input-wrap
   {:display "flex" :align-items "stretch"
    :position "relative"
    :background "#FFFFFF"
    :border-width "1px" :border-style "solid" :border-color t/--property-3
    :border-radius "12px"
    :overflow "hidden"
    :box-shadow "0 1px 2px rgba(45,62,42,0.05)"
    :transition "border-color 0.15s ease, box-shadow 0.15s ease"}
   [:&:focus-within {:border-color t/--card-sage
                     :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]]
  ;; A textarea so a long title wraps and grows the field downward rather
  ;; than sliding out of view.
  [:.title-input
   {:flex "1" :min-width "0"
    :padding "13px 16px"
    :background "transparent"
    :border "none"
    :outline "none" :resize "none"
    :font-family "inherit"
    :font-size "15px" :font-weight "500" :line-height "1.4"
    :color t/--card-forest
    :field-sizing "content"
    :overflow "hidden"}
   [:&::placeholder {:color t/--link-muted :font-weight "400"}]]
  ;; The committed title, once typing has expanded the form. Clicking it swaps
  ;; the input back in.
  [:.title-display
   {:margin "10px 0 0" :padding "0 2px"
    :font-size "22px" :font-weight "700" :line-height "1.25"
    :letter-spacing "-0.01em"
    :color t/--card-forest
    :word-break "break-word" :overflow-wrap "anywhere"
    :cursor "text"}]
  [#{:.detail-box :.detail-input}
   {:width "100%"
    :box-sizing "border-box"
    :padding "16px"
    :min-height "72px"
    :display "block" :text-align "left"
    :background "transparent"
    :border-width "1px" :border-style "dashed" :border-color "rgba(50,64,37,0.3)"
    :border-radius "12px"
    :font-family "inherit" :font-size "14px" :line-height "1.5"
    :color t/--card-forest}]
  ;; Solid, like every other control holding a value — the dashed box is the
  ;; empty "Add additional details" pill, not this.
  [:.detail-box
   {:cursor "pointer"
    :border-style "solid" :border-color t/--note-sage-55
    :white-space "pre-wrap"
    :word-break "break-word" :overflow-wrap "anywhere"
    :transition "background 0.15s, border-color 0.15s"}
   [:&:hover {:background "rgba(50,64,37,0.04)" :border-color t/--note-sage}]]
  ;; While editing, the same white chrome the title input has.
  [:.detail-input-wrap
   {:position "relative"
    :width "100%"}]
  [:.detail-input
   {:outline "none" :resize "none"
    :background "#FFFFFF"
    :border-style "solid" :border-color t/--property-3
    :box-shadow "0 1px 2px rgba(45,62,42,0.05)"
    :field-sizing "content"
    :overflow "hidden"
    :transition "border-color 0.15s ease, box-shadow 0.15s ease"}
   [:&:focus {:border-color t/--card-sage
              :box-shadow "0 0 0 3px rgba(156,174,133,0.18)"}]
   [:&::placeholder {:color t/--link-muted}]]
  ;; The extras stack: one row per extra, each growing in place, with the
  ;; urgency + date/time pair sharing a row.
  [:.detail-rows
   {:margin-top "12px"
    :display "flex" :flex-direction "column" :align-items "flex-start" :gap "10px"}
   ;; One pill height across the rows — the shared ＋ chips inherit their
   ;; notes size, so pin them up to match only inside this form.
   [sections/add-section-pill {:box-sizing "border-box" :height "24px"}]
   ;; The chip pill centers itself for chip-row company; as a direct row it
   ;; must sit with the column's left edge instead.
   [detail-pill {:align-self "flex-start"}]
   [:.detail-row
    {:display "flex" :flex-wrap "wrap" :align-items "center" :gap "8px"}]]
  [:.form-editor
   {:margin "12px auto 0"
    :width "320px"
    :display "flex" :flex-direction "column" :align-items "flex-start"
    :gap "10px"}
   ;; The task look for the shared date picker, scoped here so its other
   ;; consumers keep theirs.
   [date-picker/frame {:width "100%"}]
   [:.dp-cal-title {:font-size "17px" :font-weight "700" :color t/--humble-primary-dark}]
   [:.dp-nav-btn {:color t/--humble-primary-dark :font-size "20px"}]
   [:.dp-day {:position "relative" :font-weight "600" :border-radius "10px"}]
   ;; Today is a dot under the number rather than a ring, so it can't be read as
   ;; the pick — which is the sage outline the picker gives it.
   [".dp-day.today"
    {:box-shadow "none"}
    [:&::after
     {:content "''" :position "absolute"
      :bottom "4px" :left "50%" :transform "translateX(-50%)"
      :width "6px" :height "6px" :border-radius "999px"
      :background t/--card-sage}]]
   ;; The panel heading names the picker, so the clock's own label is noise.
   [:.dp-time-label {:display "none"}]
   [:.dp-time-box {:width "100%"}]
   [:.picker-heading
    {:font-size "14px" :font-weight "700" :color t/--humble-primary-dark}]
   [:.picker-footer
    {:align-self "stretch"
     :display "flex" :align-items "center" :justify-content "flex-end"
     :gap "18px"}]
   [:.picker-remove
    {:border "none" :background "transparent"
     :display "inline-flex" :align-items "center" :gap "6px"
     :font-family "inherit" :font-size "13px" :font-weight "600"
     :color t/--card-forest :cursor "pointer"}
    [:&:hover {:color t/--property-13}]]
   [:.picker-done
    {:padding "10px 28px"
     :border "none" :border-radius "999px"
     :background t/--humble-primary-dark :color "white"
     :font-family "inherit" :font-size "14px" :font-weight "600"
     :cursor "pointer"
     :transition "background 0.15s ease"}
    [:&:hover {:background t/--humble-primary}]]])

(defn from-task
  "A task as a draft payload: every field the composer stages. What it is
   seeded with, and what it measures having something to save against.

   `default-urgency` is the lane a task carrying none of its own starts in — a
   new-task host passes the user's saved default
   ([[hai.tasks.data/default-urgency]]) and hands [[task-form]] the same value,
   so the seed and the baseline it is measured against agree."
  ([task] (from-task task nil))
  ([task default-urgency]
   {:title       (or (:task/title task) "")
    :description (or (:task/description task) "")
    :urls        (vec (keep :activity/url (:task/activities task)))
    :contacts    (vec (:task/contacts task))
    :notes       (vec (:task/notes task))
    :tasks       (vec (:task/tasks task))
    :assignee    (:task/assigned-to task)
    :urgency     (or (:task/urgency task) default-urgency urgency/default)
    :deadline    (when (:task/deadline-at task) (deadline/from-task task))
    :tags        (vec (:task/tags task))}))

(defn task-form
  "The composer body. Collapsed it is the title field, and typing into it opens
   the extras — the first character is enough, so nothing has to be clicked or
   known to reach them (⇥ does it too, for a title still empty), and the
   expanded input keeps the focus. While expanded the title renders as a heading
   and is only an input again while it holds focus — or while it is still
   blank, since there is no text to show. The details box works the same way:
   click to edit, blur back to text.

   Everything it stages lives in the draft at `draft-id`
   ([[hai.entity.draft]]), which the host seeds; `task` is the task as filed,
   what the draft is measured against to know whether there is anything to
   save — a host that seeded a new task at the user's default urgency passes
   the same value as `default-urgency`, so the baseline agrees with the seed.
   `start-expanded?` opens past the title.

   There is one save, and it only ever fires from the shortcuts — ↵ in the
   title, ⌘/Ctrl+↵ in the details — or the Save button, which carries the one
   that works right now as key caps (`show-hint?`; off for touch). It calls
   `on-save` with the staged fields; the host closes the card once the save
   lands. `saving?` is the host's in-flight state — Save disables and spins
   while it holds, so one save goes out at a time. Blur and Escape just
   close an editor; Escape in the collapsed field also calls `on-close`.

   Save and Cancel are always there, disabled while the composer holds the task
   exactly as it stands — Save also while the title is blank.

   The extras stack one per row — details, links, urgency beside the deadline,
   assignee, tags. The assignee comes through the host's
   `render-assignee-picker` slot (the picker needs app state); `assignable?`
   false (default true) locks it — the chip loses its ✕ and the empty pill is
   inert — for a contributor, who edits the rest. The deadline date/time
   render as one joined pill even once picked. Mirrors
   [[hai.notes.form/note-form]]."
  [{:keys [draft-id task default-urgency saving? on-save on-cancel on-close
           start-expanded? show-hint? render-assignee-picker assignable?]
    :or {assignable? true}}]
  (r/with-let [!expanded? (r/atom (boolean start-expanded?))
               !editing-title? (r/atom false)
               !editing-details? (r/atom false)
               !open-sections (r/atom #{})
               !calendar-open? (r/atom false)
               !time-open? (r/atom false)
               ;; Per picker session (one open stretch of the panel): the
               ;; deadline to restore when clicking off an untouched picker,
               ;; and whether the user actually picked anything in it.
               !deadline-on-open (atom nil)
               !deadline-touched? (atom false)
               change! (fn [changes] (>e [::draft/change draft-id changes]))]
    (let [{:keys [title description urls contacts notes tasks assignee urgency deadline tags]
           :as draft}
          @(<s [::draft/draft draft-id])
          expanded? @!expanded?
          editing-title? @!editing-title?
          editing-details? @!editing-details?
          open-sections @!open-sections
          calendar-open? @!calendar-open?
          time-open? @!time-open?
          ;; Measured against the task as filed, so a composer handed unsaved
          ;; contents — the universal bar's title — has something to save from
          ;; its first render.
          unfiled? (not= draft (from-task task default-urgency))
          ;; Typing is the intent to compose, so the first character opens the
          ;; extras — nothing to click, nothing to know. `editing-title?` rides
          ;; along so the expanded form keeps showing the input being typed into
          ;; rather than swapping in the heading mid-word.
          set-title! (fn [e]
                       (let [value (.. e -target -value)]
                         (change! {:title value})
                         (when (seq (str/trim value))
                           (reset! !editing-title? true)
                           (reset! !expanded? true))))
          set-details! (fn [e] (change! {:description (.. e -target -value)}))
          set-deadline! (fn [value] (change! {:deadline value}))
          open-session! (fn []
                          (when-not (or @!calendar-open? @!time-open?)
                            (reset! !deadline-on-open deadline)
                            (reset! !deadline-touched? false)))
          open-time! (fn []
                       (open-session!)
                       (set-deadline! (update deadline :time #(or % default-deadline-time-minutes)))
                       (reset! !calendar-open? false)
                       (reset! !time-open? true))
          close-picker! (fn []
                          (set-deadline! (with-implied-date deadline))
                          (reset! !calendar-open? false)
                          (reset! !time-open? false))
          cancel-picker! (fn []
                           (set-deadline! @!deadline-on-open)
                           (reset! !calendar-open? false)
                           (reset! !time-open? false))
          remove-date! (fn []
                         (set-deadline! nil)
                         (reset! !calendar-open? false)
                         (reset! !time-open? false))
          remove-time! (fn []
                         (set-deadline! (dissoc deadline :time))
                         (reset! !time-open? false))
          save! (fn []
                  (let [trimmed-title (str/trim title)]
                    (when (and unfiled? (seq trimmed-title) (not saving?))
                      (on-save (assoc draft
                                      :title trimmed-title
                                      :deadline (with-implied-date deadline))))))
          ;; The Save shows the shortcut that saves from where the caret is:
          ;; ↵ in the title, the chord while the details editor is open.
          footer [entity-form/footer
                  [entity-form/actions
                   {:unfiled?       unfiled?
                    :saving?        saving?
                    :save-disabled? (str/blank? title)
                    :shortcut       (when show-hint?
                                      (if editing-details?
                                        (entity-form/chord "↵")
                                        ["↵"]))
                    :on-cancel      on-cancel
                    :on-save        save!}]]]
      [shell
       (if-not expanded?
         [:<>
          [:div.title-input-wrap
           [:textarea.title-input
            {:rows 1
             :auto-focus true
             :value (or title "")
             :placeholder "What's the task?"
             :on-change set-title!
             :on-key-down (fn [e]
                            (case (.-key e)
                              "Enter" (do (.preventDefault e) (save!))
                              ;; ⇥ reaches the extras from a title still empty,
                              ;; keeping it focused; ⇧⇥ stays native
                              ;; reverse-tabbing.
                              "Tab" (when-not (.-shiftKey e)
                                      (.preventDefault e)
                                      (reset! !editing-title? true)
                                      (reset! !expanded? true))
                              "Escape" (when on-close
                                         (.preventDefault e)
                                         (on-close))
                              nil))}]]
          footer]
         [:<>
          (if (or editing-title? (str/blank? title))
            [:div.title-input-wrap
             [:textarea.title-input
              {:rows 1
               :auto-focus true
               :value (or title "")
               :placeholder "What's the task?"
               :on-change set-title!
               ;; The blank title renders this editor without `editing-title?`
               ;; set; focus must set it, or the first typed character makes
               ;; the title non-blank and swaps the editor out mid-word.
               :on-focus (fn [e]
                           (reset! !editing-title? true)
                           (caret-to-end e))
               :on-blur (fn [_] (reset! !editing-title? false))
               :on-key-down (fn [e]
                              (case (.-key e)
                                "Enter" (do (.preventDefault e) (save!))
                                "Escape" (do (.preventDefault e) (.. e -target blur))
                                nil))}]]
            [:h2.title-display
             {:on-click #(reset! !editing-title? true)}
             title])
          [:div.detail-rows
           (cond
             editing-details?
             [:div.detail-input-wrap
              [:textarea.detail-input
               {:auto-focus true
                :value (or description "")
                :placeholder "Add details"
                :on-change set-details!
                :on-focus caret-to-end
                :on-blur (fn [_] (reset! !editing-details? false))
                :on-key-down (fn [e]
                               (cond
                                 (keyboard/submit-shortcut? e)
                                 (do (.preventDefault e) (save!))

                                 (= "Escape" (.-key e))
                                 (do (.preventDefault e) (.. e -target blur))))}]]

             ;; Enter edit on mousedown: when the title editor is focused, its
             ;; blur reflows the layout mid-click and the click never completes
             ;; on the moved control. on-click stays for keyboard activation.
             (str/blank? description)
             [detail-pill
              {:type "button"
               :on-mouse-down (fn [e]
                                (when (zero? (.-button e))
                                  (reset! !editing-details? true)))
               :on-click #(reset! !editing-details? true)}
              [details-icon]
              "Add additional details"]

             :else
             [:button.detail-box
              {:type "button"
               :on-mouse-down (fn [e]
                                (when (zero? (.-button e))
                                  (reset! !editing-details? true)))
               :on-click #(reset! !editing-details? true)}
              description])
           ;; Every pill keeps the focused editor's focus through the click —
           ;; the blur would reflow the layout mid-click and swallow it (same
           ;; trap as the details box). The links and tags rows are the shared
           ;; sections notes renders; only the handlers differ — writing into
           ;; the draft until its one save.
           [sections/links-section
            {:link-urls urls
             :contacts contacts
             :linked-notes notes
             :linked-tasks tasks
             :link-tooltip-text "Attach a contact, note, task or page to this task"
             :editing? true
             :open? (contains? open-sections :links)
             :on-open #(swap! !open-sections conj :links)
             :on-cancel #(swap! !open-sections disj :links)
             :on-add (fn [link-url]
                       (when-not (some #{link-url} urls)
                         (change! {:urls (conj (vec urls) link-url)})))
             :on-remove (fn [link-url]
                          (change! {:urls (vec (remove #{link-url} urls))}))
             :on-add-contact (fn [contact]
                               (change! {:contacts (conj (vec contacts) contact)}))
             :on-remove-contact (fn [contact]
                                  (change! {:contacts (vec (remove #(= (:contact/uuid contact)
                                                                       (:contact/uuid %))
                                                                   contacts))}))
             :on-add-note (fn [note] (change! {:notes (conj (vec notes) note)}))
             :on-remove-note (fn [note]
                               (change! {:notes (vec (remove #(= (:note/uuid note) (:note/uuid %))
                                                             notes))}))
             :on-add-task (fn [linked-task]
                            (change! {:tasks (conj (vec tasks) linked-task)}))
             :on-remove-task (fn [linked-task]
                               (change! {:tasks (vec (remove #(= (:task/uuid linked-task)
                                                                 (:task/uuid %))
                                                             tasks))}))
             :render-entity-search link-combobox/entity-search}]
           [:div.detail-row
            [urgency-pill
             [:span.urgency-glyph [urgency-icon]]
             (for [{:keys [value label]} urgency/options]
               ^{:key value}
               [:button {:type "button"
                         :class (when (= value urgency) "active")
                         :on-mouse-down #(.preventDefault %)
                         :on-click #(change! {:urgency value})}
                label])]
            [deadline-pill
             {:class (when (or (:date deadline) (:time deadline)) "filled")}
             (let [date (:date deadline)]
               [:span.half {:class (when date "active")}
                ;; The segs preventDefault their clicks so the panel's outside-
                ;; click handler reads them as handled — they own picker state.
                [:button {:type "button"
                          :on-mouse-down #(.preventDefault %)
                          :on-click (fn [e]
                                      (.preventDefault e)
                                      (if @!calendar-open?
                                        (reset! !calendar-open? false)
                                        (do (open-session!)
                                            (reset! !time-open? false)
                                            (reset! !calendar-open? true))))}
                 [deadline-icon]
                 (if date
                   (datetime/date-label (datetime/value->moment {:date date}))
                   "Set date")]
                (when date
                  [:button.seg-remove {:type "button"
                                       :aria-label "Remove date"
                                       :title "Remove date"
                                       :on-mouse-down #(.preventDefault %)
                                       :on-click (fn [e]
                                                   (.preventDefault e)
                                                   (remove-date!))}
                   "✕"])])
             [:span.split-divider]
             (let [minutes (:time deadline)]
               [:span.half {:class (when minutes "active")}
                [:button {:type "button"
                          :on-mouse-down #(.preventDefault %)
                          :on-click (fn [e]
                                      (.preventDefault e)
                                      (open-time!))}
                 [datetime/clock-icon]
                 (if minutes
                   (date-picker/format-minutes minutes)
                   "Set time")]
                (when minutes
                  [:button.seg-remove {:type "button"
                                       :aria-label "Remove time"
                                       :title "Remove time"
                                       :on-mouse-down #(.preventDefault %)
                                       :on-click (fn [e]
                                                   (.preventDefault e)
                                                   (remove-time!))}
                   "✕"])])]]
           (if assignee
             ;; Picked: the standard chip, whose ✕ clears the assignment back
             ;; to the pill.
             [chip-ui/entity-chip
              {:glyph [person-icon]
               :label (assignee-label assignee)
               :remove-label "Remove assignee"
               :on-remove (when assignable? #(change! {:assignee nil}))}]
             ;; A span with the pill's class, not the pill itself — the
             ;; picker wraps the trigger in its own button, and a button
             ;; can't nest inside a button.
             (let [trigger [:span {:class (str detail-pill)
                                   :on-mouse-down #(.preventDefault %)}
                            [person-icon]
                            "Who's doing this?"]]
               (cond
                 (not assignable?)
                 [:span {:class [(str detail-pill) "locked"]
                         :title "Only an admin can assign this task"}
                  [person-icon]
                  "Who's doing this?"]

                 render-assignee-picker
                 (render-assignee-picker {:on-pick #(change! {:assignee %})
                                          :trigger trigger})

                 :else trigger)))
           [sections/tags-section
            {:tags tags
             :tag-tooltip-text "Tag this task to find it with related work"
             :editing? true
             :open? (contains? open-sections :tags)
             :on-open #(swap! !open-sections conj :tags)
             :on-cancel #(swap! !open-sections disj :tags)
             :on-pick (fn [tag] (change! {:tags (conj (vec tags) tag)}))
             :on-remove (fn [tag]
                          (change! {:tags (vec (remove #(= % tag) tags))}))
             :render-tag-search tag-combobox/search-slot}]]
          (when (or calendar-open? time-open?)
            ;; Clicking off the panel works as its Done button once something
            ;; was picked this session; an untouched picker cancels — the
            ;; deadline goes back to its pre-open value (dropping the seeded
            ;; time default) and the panel closes.
            [click-outside/area
             {:class "form-editor"
              :on-mouse-down keep-picker-focus!
              :on-click-out (fn []
                              (if @!deadline-touched?
                                (close-picker!)
                                (cancel-picker!)))}
             [:div.picker-heading (if calendar-open? "Pick a day" "Pick a time")]
             [date-picker/date-picker
              {:value deadline
               :on-change (fn [v]
                            (reset! !deadline-touched? true)
                            (set-deadline! v))
               :show-mode-toggle? false
               :show-quick-chips? false
               :show-calendar? calendar-open?
               :show-time? time-open?}]
             [:div.picker-footer
              (if calendar-open?
                [:button.picker-remove
                 {:type "button" :on-click remove-date!}
                 "✕ Remove date"]
                [:button.picker-remove
                 {:type "button" :on-click remove-time!}
                 "✕ Remove time"])
              [:button.picker-done
               {:type "button" :on-click close-picker!}
               "Done"]]])
          footer])])))
